/* import __COLOCATED_TEMPLATE__ from './edit-assignment-limit.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { post } from 'embercom/lib/ajax';
import ENV from 'embercom/config/environment';
import type IntlService from 'embercom/services/intl';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';

interface Args {
  teammates: any[];
  workspaceCapacityLimit: number;
  bulkUpdateLimit: number;
  limitType?: 'assignment_limit' | 'ticket_assignment_limit';
  usesCustomTicketAssignmentLimit?: boolean;
}

export default class EditAssignmentLimit extends Component<Args> {
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare intl: IntlService;
  @service declare permissionsService: any;
  @service declare intercomEventService: any;
  @service declare intercomConfirmService: any;
  @service declare modelDataCacheService: any;

  @tracked assignmentLimit = '';
  @tracked selectedTeammates: any[] = [];
  @tracked valueIsModified = false;
  @tracked showUpdateNumber = false;
  @tracked popoverOpen = false;

  get limitType() {
    return this.args.limitType || 'assignment_limit';
  }

  get translationKey() {
    return `settings.teammate-workload.${this.limitType.replaceAll('_', '-')}`;
  }

  get translationLabel() {
    if (this.args.usesCustomTicketAssignmentLimit && this.limitType === 'assignment_limit') {
      return this.intl.t('settings.teammate-workload.assignment-limit.conversation-edit-label');
    }

    return this.intl.t(`${this.translationKey}.edit-label`);
  }

  get cantChangeAssignmentLimit() {
    if (this.limitType === 'ticket_assignment_limit') {
      return (
        !this.permissionsService.currentAdminCan('can_manage_workload_management') ||
        !this.args.usesCustomTicketAssignmentLimit
      );
    }

    return !this.permissionsService.currentAdminCan('can_manage_workload_management');
  }

  @action popoverClosed() {
    this.showUpdateNumber = false;
    this.popoverOpen = false;
  }

  @action
  prepareUpdate() {
    this.resetState();

    this.selectedTeammates = this.args.teammates.filter((teammate) => {
      return teammate.isSelected;
    });

    this.assignmentLimit = '';
  }

  get assignmentLimitInvalid() {
    return (
      !this.assignmentLimit ||
      parseInt(this.assignmentLimit, 10) > 1_000_000 ||
      parseInt(this.assignmentLimit, 10) < 0
    );
  }

  get bulkUpdateAllowed() {
    let selectedTeammatesCount = this.args.teammates.filter((teammate) => {
      return teammate.isSelected;
    }).length;
    return selectedTeammatesCount <= this.args.bulkUpdateLimit;
  }

  @action
  async pushUpdates() {
    if (!(await this.askConfirmation())) {
      return;
    }

    try {
      let teammates = this.selectedTeammates.map((teammate) => teammate.id);
      this.notificationsService.notifyConfirmation(
        this.intl.t(`${this.translationKey}.updating`, {
          count: teammates.length,
        }),
        ENV.APP._1M * 60,
      ); // 1 hour

      let assignmentLimit =
        this.assignmentLimit !== `${this.args.workspaceCapacityLimit}`
          ? this.assignmentLimit
          : null;
      await post(`/ember/admins/change_${this.limitType}`, {
        [this.limitType]: assignmentLimit,
        admin_ids: teammates,
        id: this.appService.app.currentAdmin.id,
        app_id: this.appService.app.id,
      });

      this.selectedTeammates.forEach((teammate) => {
        teammate.workload_management_settings = {
          ...teammate.workload_management_settings,
          [this.limitType]: assignmentLimit ? parseInt(assignmentLimit, 10) : null,
        };
      });

      this.notificationsService.clear();
      this.notificationsService.notifyConfirmation(
        this.intl.t(`${this.translationKey}.updated`, {
          count: teammates.length,
        }),
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'updated',
        object: this.limitType,
        place: 'teammate_workload',
        teammate_ids: teammates,
        selected_teammate_count: teammates.length,
        [this.limitType]: assignmentLimit,
      });

      this.resetState();
    } catch (error) {
      this.notificationsService.clear();
      if (error.jqXHR) {
        this.notificationsService.notifyError(error.jqXHR.responseJSON.errors);
      }
    } finally {
      this.appService.app.updateLocalCache(this.modelDataCacheService);
    }
  }

  async askConfirmation() {
    let backToWorkspaceLimit =
      this.assignmentLimit === null ||
      parseInt(this.assignmentLimit, 10) === this.args.workspaceCapacityLimit;
    return await this.intercomConfirmService.confirm({
      title: this.intl.t(`${this.translationKey}.confirmation.title`),
      body: sanitizeHtml(
        backToWorkspaceLimit
          ? this.intl.t(`${this.translationKey}.confirmation.body-revert`, {
              workspaceLimit: this.args.workspaceCapacityLimit,
              teammateCount: this.selectedTeammates.length,
            })
          : this.intl.t(`${this.translationKey}.confirmation.body`, {
              assignmentLimit: this.assignmentLimit,
              teammateCount: this.selectedTeammates.length,
            }),
      ),
      cancelButtonText: this.intl.t(`${this.translationKey}.confirmation.cancel`),
      confirmButtonText: this.intl.t(`${this.translationKey}.confirmation.confirm`),
    });
  }

  resetState() {
    this.assignmentLimit = '';
    this.valueIsModified = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox::TeammateWorkload::BulkActions::EditAssignmentLimit': typeof EditAssignmentLimit;
    'inbox/teammate-workload/bulk-actions/edit-assignment-limit': typeof EditAssignmentLimit;
  }
}
